import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { resolveContext, useSession } from '@/state/session';
import { MORE_GROUP_ORDER, visibleDestinations, type NavGroup } from '@/app/navigation';
import { CAPABILITIES, resolveCapability } from '@/services/entitlements';
import { Initials, ListRow, SectionHeader, StatusBadge } from '@/components/banking';
import { ConfirmSheet } from '@/components/feedback/Sheet';
import { Notice } from '@/components/feedback';
import { moreIconFor } from './MoreIcons';

/**
 * The "More" hub.
 *
 * TRACE:TT-R128-01 — the More section and its sub-sections exist and are
 * reachable from the mobile shell.
 * TRACE:TT-R145-03 — "More" is used only as a navigation destination. The
 * return control is always labelled "Back"; a "get more information" affordance
 * is never called "More", so the two cannot be confused.
 */
export function MorePage() {
  const navigate = useNavigate();
  const resolved = resolveContext();
  const signOut = useSession((s) => s.signOut);
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  if (!resolved) return null;

  const destinations = visibleDestinations(resolved.entitlements, 'more');
  const grouped = MORE_GROUP_ORDER.map((group) => ({
    group,
    items: destinations.filter((d) => d.group === group),
  })).filter((g) => g.items.length > 0);

  const companyCount = resolved.user.companyIds.length;

  return (
    <div className="tm-stack">
      <header className="tm-header tm-header--home" style={{ marginInline: 'calc(var(--tm-gutter) * -1)' }}>
        <h1 className="tm-header__title">More</h1>
      </header>

      {/* Company context and switcher.
          TRACE:XS-SMEM-WR-032 — switch authorised companies without re-login. */}
      <div className="tm-card tm-card--flush">
        <ListRow
          leading={<Initials name={resolved.company.tradingName} />}
          title={resolved.company.tradingName}
          meta={
            companyCount > 1
              ? `${resolved.user.name} · Switch company`
              : `${resolved.user.name} · ${resolved.company.legalName}`
          }
          end={resolved.company.status === 'active' ? null : <StatusBadge status={resolved.company.status} />}
          {...(companyCount > 1
            ? {
                onClick: () => {
                  useSession.setState({ phase: 'awaiting-company', returnTo: '/mobile/more' });
                  navigate('/mobile/select-company');
                },
                ariaLabel: `Signed in to ${resolved.company.tradingName} as ${resolved.user.name}. Switch company.`,
              }
            : {})}
        />
      </div>

      {resolved.company.status !== 'active' && resolved.company.statusReason ? (
        <Notice tone="warning" title={`Company ${resolved.company.status}`}>
          {resolved.company.statusReason}
        </Notice>
      ) : null}

      {grouped.map(({ group, items }) => (
        <section key={group} aria-labelledby={`more-${group}`}>
          <SectionHeader title={group} id={`more-${group}`} />
          <div className="tm-card tm-card--flush">
            <ul className="tm-rows">
              {items.map((item) => {
                const denied =
                  item.capability &&
                  !resolveCapability(resolved.entitlements, CAPABILITIES[item.capability]).allowed;
                return (
                  <li key={item.id}>
                    <ListRow
                      to={item.route}
                      leading={<span className="tm-more-icon">{moreIconFor(item.id)}</span>}
                      title={item.label}
                      meta={item.description}
                      end={
                        item.handoff ? (
                          <span className="tm-hint">Opens web</span>
                        ) : denied ? (
                          <StatusBadge status="restricted" label="Limited" tone="warning" />
                        ) : null
                      }
                      ariaLabel={
                        item.description ? `${item.label}. ${item.description}` : item.label
                      }
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      ))}

      <button
        type="button"
        className="tm-btn tm-btn--secondary tm-btn--block"
        onClick={() => setConfirmSignOut(true)}
      >
        Sign out
      </button>

      <ConfirmSheet
        open={confirmSignOut}
        onClose={() => setConfirmSignOut(false)}
        onConfirm={() => {
          signOut();
          navigate('/mobile/login', { replace: true });
        }}
        title="Sign out?"
        body="You will need your password and a security code to sign back in."
        confirmLabel="Sign out"
      />
    </div>
  );
}

export type { NavGroup };
